// Define an interface for an edge in the graph.
interface Edge {
   from: string;
   to: string;
   weight: number;
   index: number;
}

// Define a type alias for each row in the data (three string elements).
export type DataRow = [string, string, number];

/**
 * Function to remove cycles from a graph.
 * If a bidirectional edge (a simple two-node cycle) is detected,
 * it merges the weight of the removed edge into its reverse edge.
 * @param data - Array of data rows, where the first row is the header.
 * @returns A new data array without cyclic edges.
 */
export function removeCyclesAndMerge(data: DataRow[]): DataRow[] {
   const header = data[0];
   const rawEdges = data.slice(1);

   // Transform raw edges into Edge objects with an index for tracking purposes.
   const edges: Edge[] = rawEdges.map((edge, i) => ({
      from: edge[0],
      to: edge[1],
      weight: Number(edge[2]),
      index: i,
   }));

   // Set to store the indices of removed edges.
   const removedEdges = new Set<number>();

   // Function to build the graph.
   // The graph is represented as an object where keys are node names and
   // values are arrays of outgoing edges.
   function buildGraph(): {[node: string]: Edge[]} {
      const graph: {[node: string]: Edge[]} = {};
      for (const edge of edges) {
         if (removedEdges.has(edge.index)) continue;
         if (!graph[edge.from]) graph[edge.from] = [];
         graph[edge.from].push(edge);
         // Ensure that the destination node is present in the graph even if it has no outgoing edges.
         if (!graph[edge.to]) graph[edge.to] = [];
      }
      return graph;
   }

   /**
    * Function to find a cycle in the graph using DFS.
    * If a cycle is found, it returns an array of edges that form the cycle.
    */
   function findCycle(graph: {[node: string]: Edge[]}): Edge[] | null {
      const visited = new Set<string>();
      const recStack = new Set<string>();
      const parent: {[node: string]: Edge} = {};

      function dfs(node: string): Edge[] | null {
         visited.add(node);
         recStack.add(node);

         if (graph[node]) {
            for (const edge of graph[node]) {
               // If the node has not been visited, go deeper.
               if (!visited.has(edge.to)) {
                  parent[edge.to] = edge;
                  const cycle = dfs(edge.to);
                  if (cycle) return cycle;
               }
               // If the node is already in the recursion stack, a cycle is detected.
               else if (recStack.has(edge.to)) {
                  // Reconstruct the cycle starting with the current edge.
                  const cycleEdges: Edge[] = [];
                  cycleEdges.push(edge);
                  let current = node;
                  while (current !== edge.to) {
                     const pe = parent[current];
                     cycleEdges.push(pe);
                     current = pe.from;
                  }
                  return cycleEdges;
               }
            }
         }
         recStack.delete(node);
         return null;
      }

      for (const node in graph) {
         if (!visited.has(node)) {
            const cycle = dfs(node);
            if (cycle) return cycle;
         }
      }
      return null;
   }

   // Main loop: search for a cycle; if found, remove one edge (merging weight if applicable) and repeat.
   let graph = buildGraph();
   let cycle = findCycle(graph);

   while (cycle) {
      // Choose the first edge in the detected cycle.
      const edgeToRemove = cycle[0];
      // Look for a reverse edge: from the destination back to the source.
      const reverseEdge = edges.find(
         (edge) =>
            edge.from === edgeToRemove.to &&
            edge.to === edgeToRemove.from &&
            !removedEdges.has(edge.index)
      );

      if (reverseEdge) {
         // Merge weights: add the weight of the edge to be removed to the reverse edge's weight.
         const mergedWeight = Number(reverseEdge.weight) + Number(edgeToRemove.weight);
         reverseEdge.weight = mergedWeight;
         console.log(
            `Merging edge weight: merging ${edgeToRemove.from} -> ${edgeToRemove.to} (weight: ${edgeToRemove.weight}) into reverse edge ${reverseEdge.from} -> ${reverseEdge.to} (new weight: ${reverseEdge.weight})`
         );
      } else {
         console.log(
            `Removing cycle edge: ${edgeToRemove.from} -> ${edgeToRemove.to} (weight: ${edgeToRemove.weight})`
         );
      }

      removedEdges.add(edgeToRemove.index);
      // Rebuild the graph after removal/merging.
      graph = buildGraph();
      cycle = findCycle(graph);
   }

   // Create a new dataset without the removed edges.
   const newEdges = edges
      .filter((edge) => !removedEdges.has(edge.index))
      .map((edge) => [edge.from, edge.to, edge.weight] as DataRow);

   return [header, ...newEdges];
}
